import type { ReactNode } from "react";
import { GlassMaterial } from "@link/ui";
import { Camera, CameraOff, Headphones, HeadphoneOff, Mic, MicOff, MonitorUp, Phone, PhoneOff, Popcorn, Square } from "lucide-react";

export interface CallControlsProps {
  mode: "incoming" | "outgoing" | "active";
  connected: boolean;
  connecting: boolean;
  microphoneEnabled: boolean;
  cameraEnabled: boolean;
  deafened: boolean;
  screenShareEnabled: boolean;
  watchActive: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onCancel: () => void;
  onMicrophone: () => void;
  onCamera: () => void;
  onDeafen: () => void;
  onScreenShare: () => void;
  onWatch: () => void;
  onRejoin: () => void;
  onLeave: () => void;
}

/** View-only controls. MediaSession remains the owner of every real media action. */
export function CallControls(props: CallControlsProps) {
  const unavailable = !props.connected || props.connecting;
  return (
    <div className="call-control-dock liquid-lens-host relative" role="group" aria-label="Call controls">
      <GlassMaterial radius={36} />
      {props.mode === "incoming" ? <>
        <button type="button" className="call-action call-action-danger" disabled={props.connecting} onClick={props.onDecline}><PhoneOff aria-hidden="true" />Decline</button>
        <button type="button" className="call-action call-action-positive" disabled={props.connecting} onClick={props.onAccept}><Phone aria-hidden="true" />Accept</button>
      </> : props.mode === "outgoing" ? (
        <button type="button" className="call-action call-action-danger" onClick={props.onCancel}><PhoneOff aria-hidden="true" />Cancel</button>
      ) : <>
        <Toggle label={props.microphoneEnabled ? "Mute microphone" : "Unmute microphone"} pressed={!props.microphoneEnabled} disabled={unavailable} onClick={props.onMicrophone}>{props.microphoneEnabled ? <Mic /> : <MicOff />}</Toggle>
        <Toggle label={props.cameraEnabled ? "Turn camera off" : "Turn camera on"} pressed={props.cameraEnabled} disabled={unavailable} onClick={props.onCamera}>{props.cameraEnabled ? <Camera /> : <CameraOff />}</Toggle>
        <Toggle label={props.deafened ? "Undeafen" : "Deafen"} pressed={props.deafened} disabled={unavailable} onClick={props.onDeafen}>{props.deafened ? <HeadphoneOff /> : <Headphones />}</Toggle>
        <Toggle label={props.screenShareEnabled ? "Stop sharing screen" : "Share screen"} pressed={props.screenShareEnabled} disabled={unavailable} onClick={props.onScreenShare}>{props.screenShareEnabled ? <Square /> : <MonitorUp />}</Toggle>
        <Toggle label="Watch party" pressed={props.watchActive} disabled={unavailable} onClick={props.onWatch}><Popcorn /></Toggle>
        <span className="call-control-divider" aria-hidden="true" />
        {!props.connected && <button type="button" className="call-action call-action-positive" disabled={props.connecting} onClick={props.onRejoin}><Phone aria-hidden="true" />{props.connecting ? "Joining…" : "Rejoin"}</button>}
        <button type="button" className="call-action call-action-danger" onClick={props.onLeave} aria-label="Leave call" title="Leave call"><PhoneOff aria-hidden="true" /><span>Leave</span></button>
      </>}
    </div>
  );
}

function Toggle({ label, pressed, disabled, onClick, children }: {
  label: string; pressed: boolean; disabled: boolean; onClick: () => void; children: ReactNode;
}) {
  return <button type="button" className="call-control" aria-label={label} title={label} aria-pressed={pressed} disabled={disabled} onClick={onClick}>{children}</button>;
}
